const QUEUE_SOURCE = { shipping: 0, warranty: 1, other: 2 };

class CartOperationsQueue {
  static instance;

  #queues = new Map();           // <productId, [{operation, resolve, reject, priority}]>
  #activeOperations = new Map(); // <productId, boolean>
  #listeners = new Set();

  constructor() {
    if (CartOperationsQueue.instance) {
      return CartOperationsQueue.instance;
    }
    CartOperationsQueue.instance = this;
  }

  static getInstance() {
    if (!CartOperationsQueue.instance) {
      new CartOperationsQueue();
    }
    return CartOperationsQueue.instance;
  }

  subscribe(listener) {
    this.#listeners.add(listener);
    listener(this.getStatus());

    return () => {
      this.#listeners.delete(listener);
    };
  }

  #notify() {
    if (!this.#listeners.size) {
      return
    }

    const snapshot = this.getStatus();
    this.#listeners.forEach((listener) => listener(snapshot));
  }

  enqueueProductOperation(productIdOld, operation, options = {}) {
    const productId = 'GLOBAL_SINGLE_QUEUE'
    const { source = 'other' } = options;
    const priority = QUEUE_SOURCE[source] ?? QUEUE_SOURCE.other;

    return new Promise((resolve, reject) => {
      const item = { operation, resolve, reject, priority, source };

      if (!this.#queues.has(productId)) {
        this.#queues.set(productId, []);
      }

      this.#queues.get(productId).push(item);
      this.#queues.get(productId).sort((a, b) => (a.priority ?? QUEUE_SOURCE.other) - (b.priority ?? QUEUE_SOURCE.other));

      const queueSnapshot = this.#queues.get(productId).map(i => i.source);
      const isActive = this.#activeOperations.has(productId);

      window.curacaoLogger?.log('INFO', '[CartOperationsQueue] Enqueued', {
        productId,
        source,
        priority,
        isActive,
        pendingQueue: queueSnapshot,
      });

      this.#tryStart(productId);
      this.#notify()
    });
  }

  #tryStart(productId) {
    if (this.#activeOperations.has(productId)) return;

    const q = this.#queues.get(productId);
    if (!q || q.length === 0) return;

    const item = q.shift();
    this.#activeOperations.set(productId, true);

    window.curacaoLogger?.log('INFO', '[CartOperationsQueue] Starting operation', {
      productId,
      source: item.source,
      remainingInQueue: q.map(i => i.source),
    });

    void this.#runOperation(productId, item);
  }

  async #runOperation(productId, item) {
    try {
      const res = await item.operation();
      item.resolve(res);
    } catch (err) {
      item.reject(err);
      window.curacaoLogger?.log('INFO', '[CartOperationsQueue] Operation failed', {
        productId,
        source: item.source,
        nextInQueue: (this.#queues.get(productId) || []).map(i => i.source),
      })
    } finally {
      this.#activeOperations.delete(productId);
      window.curacaoLogger?.log('INFO', '[CartOperationsQueue] Operation finished', {
        productId,
        source: item.source,
        nextInQueue: (this.#queues.get(productId) || []).map(i => i.source),
      });
      this.#notify()
      this.#tryStart(productId);
    }
  }

  getStatus() {
    let queued = 0;
    this.#queues.forEach(q => queued += q.length);

    return {
      queued,
      active: this.#activeOperations.size,
      queues: this.#queues.size
    };
  }
}

window.cartOperationsQueue = CartOperationsQueue.getInstance();
